/**
 * SaveTags — the optional judgment tags (T-12, M-TAG1):
 * criticality, responsible party, phase, trade, decision maker and, without a
 * photo, the room tag. None has a default or an extraction-sourced value (AC-1g-06).
 */

import { Chip } from "./Chip";
import { PHASE_OPTIONS } from "../read/tag-filter";
import { DECISION_MAKER_OPTIONS } from "../triage/save-screen-types";

export interface SaveTagsProps {
  criticalityTier: string;
  ownerDraft: string;
  phaseDraft: string;
  tradeDraft: string;
  decidesDraft: string;
  roomTagDraft: string;
  hasPhotoPart?: boolean;
  handleCriticalityChip: (tier: string) => void;
  handleOwnerInput: (value: string) => void;
  handlePhaseChange: (value: string) => void;
  handleTradeInput: (value: string) => void;
  handleDecidesChange: (value: string) => void;
  handleRoomTagInput: (value: string) => void;
}

export function SaveTags({
  criticalityTier,
  ownerDraft,
  phaseDraft,
  tradeDraft,
  decidesDraft,
  roomTagDraft,
  hasPhotoPart,
  handleCriticalityChip,
  handleOwnerInput,
  handlePhaseChange,
  handleTradeInput,
  handleDecidesChange,
  handleRoomTagInput,
}: SaveTagsProps) {
  return (
    <>
      <section data-testid="save-screen-judgment" class="save-chip-group">
        <p class="save-chip-group-hint">Setting tags is optional</p>
        {/* §5.1/§7.5: criticality is tappable pill chips (aria-pressed, no
            default — AC-1g-06). The design's Normal/Critical maps onto the
            shared criticality_tier vocabulary (Normal → "medium",
            Critical → "high"); the finer "low" tier is set only on the
            Master List detail surface. */}
        <div class="save-chip-row" role="group" aria-label="Criticality" data-testid="save-screen-criticality">
          <p class="save-chip-group-hint" style="margin:0 0 8px 0;width:100%">Criticality</p>
          <Chip
            data-testid="save-screen-criticality-normal"
            aria-pressed={criticalityTier === "medium"}
            onClick={() => handleCriticalityChip("medium")}
          >
            Normal
          </Chip>
          <Chip
            data-testid="save-screen-criticality-critical"
            aria-pressed={criticalityTier === "high"}
            onClick={() => handleCriticalityChip("high")}
          >
            Critical
          </Chip>
        </div>
        <label>
          {/* "Owner" means the PROPERTY owner / client in construction, so using
              it for an assignee misleads the people this is built for
              (field feedback, 2026-07-27). Display copy only — the wire field
              stays `owner`. */}
          Responsible Party
          <input
            type="text"
            data-testid="save-screen-owner-input"
            value={ownerDraft}
            onInput={(e) => handleOwnerInput((e.target as HTMLInputElement).value)}
          />
        </label>
        {/* M-TAG1 (BUI-92): the FIRST tagging tier — phase + trade — brought to
            desktop parity with mobile. Phase is a FIXED picker off the
            Excel-sourced master decision list (PHASE_OPTIONS), so the value set
            matches mobile and the downstream coverage view has a stable enum to
            group by. Trade is free-text, mirroring the Master List detail's
            Trade input. Both ride the SAME onJudgmentChange dirty-tracked emit
            as criticality/owner and are NEVER seeded from `extraction`
            (AC-1g-06). */}
        <label>
          Phase
          <select
            data-testid="save-screen-phase-select"
            value={phaseDraft}
            onChange={(e) => handlePhaseChange((e.target as HTMLSelectElement).value)}
          >
            <option value="">— Select phase —</option>
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Trade
          <input
            type="text"
            data-testid="save-screen-trade-input"
            value={tradeDraft}
            onInput={(e) => handleTradeInput((e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Decision maker
          <select data-testid="save-screen-decides-select" value={decidesDraft} onChange={(e) => handleDecidesChange((e.target as HTMLSelectElement).value)}>
            <option value="">— Select decision maker —</option>
            {DECISION_MAKER_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        {!hasPhotoPart && (
          <label>
            Room tag
            <input type="text" data-testid="save-screen-room-tag-input" value={roomTagDraft} onInput={(e) => handleRoomTagInput((e.target as HTMLInputElement).value)} />
          </label>
        )}
      </section>
    </>
  );
}
